import React, { ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export type GenerateReportDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  header?: string | null | ReactNode;
  text?: string | null | ReactNode;
  isLoading?: boolean;
  isInformationalOnly?: boolean;
  cancelText?: string | null;
  proceedText?: string | null;
  type?: 'information' | 'error';
  closeOnSuccess?: boolean;
  forceNonDismissible?: boolean;
};

function AcceptPlanModal({
  open,
  onClose,
  onSuccess,
  header,
  isLoading = false,
  text,
  isInformationalOnly = false,
  cancelText = 'Cancel',
  proceedText = 'OK',
  type = 'information',
  closeOnSuccess = true,
  forceNonDismissible = false,
}: GenerateReportDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const getBackgroundColor = () => {
    if (isLoading) return undefined;
    return type === 'information' ? '#0183E8' : '#E5484D';
  };

  const titleColor =
    type === 'information' ? '#447EEB' : theme.palette.error.main;

  const titleWrapperColor =
    type === 'information'
      ? theme.palette.primary.main
      : theme.palette.error.main;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (
          (isLoading || forceNonDismissible) &&
          (reason === 'backdropClick' || reason === 'escapeKeyDown')
        ) {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown={isLoading || forceNonDismissible}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: { borderRadius: '10px', padding: '16px', width: '500px' },
      }}
    >
      {(header || type === 'error') && (
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            display: 'flex',
            color: titleWrapperColor,
          }}
        >
          <Typography
            sx={{
              margin: 0,
              alignSelf: 'self-end',
              fontSize: '28px',
              fontWeight: 700,
              color: titleColor,
            }}
          >
            {type === 'error' ? 'Accept failed' : header}
          </Typography>
        </DialogTitle>
      )}

      <DialogContent>
        <DialogContentText
          id="alert-dialog-description"
          sx={{ color: '#525F81', fontWeight: 400 }}
        >
          <Typography whiteSpace="pre-line">{text}</Typography>
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ gap: '1rem' }}>
        {!isInformationalOnly && (
          <Button
            onClick={onClose}
            size="large"
            variant="text"
            disabled={isLoading}
            sx={{
              backgroundColor:
                type === 'error'
                  ? theme.palette.ctaButton.background
                  : '#0000330F',
              minWidth: '100px',
              borderRadius: 1,
              color:
                type === 'error'
                  ? theme.palette.primary.contrastText
                  : '#60646C',
              height: '42px',

              '&:hover': {
                background: theme.palette.ctaButton.hoverBackground,
              },
            }}
          >
            {cancelText ?? t('common.cancel')}
          </Button>
        )}

        {type !== 'error' && (
          <Button
            onClick={() => {
              onSuccess();
              if (closeOnSuccess) onClose();
            }}
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              backgroundColor: getBackgroundColor(),
              minWidth: '150px',
              height: '42px',
              borderRadius: 1,
            }}
          >
            {isLoading ? (
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            ) : (
              proceedText ?? t('common.ok')
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AcceptPlanModal;
